import { toast } from "sonner";
import { SocketEvents } from "../socketEvents";
import { useSocketListener } from "./useSocketListener";
import { refetchLeads } from "@/features/leads/api/useLeads";
import { refetchChilds } from "@/features/leads/api/useChilds";
import { removeChildFromCaches } from "@/features/leads/api/useChildMutations";
import { refetchWhatsappLeads } from "@/features/whatsapp/api/useWhatsappContacts";
import { refetchCurrentWhatsappMessage } from "@/features/whatsapp/api/useCurrentWhatsappMsgs";
import {
  queryClient,
  invalidateLeadCaches,
  invalidateLeadTimeline,
  invalidateLeadTimelines,
  refetchEntityTracksAfterAutomation,
} from "@/lib/queryClient";
import { currentUserId } from "@/lib/currentUserId";
import { refreshSessionUserAndInvalidateQueries } from "@/lib/refreshSessionUser";
import {
  applyNotificationPush,
  SHUFFLE_WARNINGS_KEY,
} from "@/features/notifications/api/useNotifications";
import type { NotificationPush } from "@/features/notifications/api/notificationCachePatch";
import {
  applyAlertNewPush,
  applyAlertUpdatedPush,
} from "@/features/alerts/api/useAlerts";
import type { AlertNewPush, AlertUpdatedPush } from "@/features/alerts/types";
import {
  enqueueToneAlert,
  type ToneAlertPayload,
} from "@/features/notifications/tone/toneAlert";
import {
  enqueueRecaptureAlert,
  type RecaptureAlertPayload,
} from "@/features/notifications/recapture/recaptureAlert";
import { playChatSound } from "@/features/chat/utils/chatNotificationSound";
import { claimChatAlert } from "@/features/chat/utils/chatAlertDedupe";
import { showNewMessageToast } from "@/features/chat/components/NewMessageToast";
import {
  pushChatPopupAlert,
  type ChatPopupKind,
} from "@/features/chat/alerts/chatPopupAlert";
import { chatAlertKey } from "@/features/chat/utils/chatAlertDedupe";
import { requestOpenChat } from "@/features/chat/utils/chatOpenBus";
import { openChatForLead } from "@/features/whatsapp/stores/whatsappSlice";
import { globalNavigate } from "@/routes/globalNavigate";
import { store } from "@/stores/store";
import { pushDueFollowUp } from "@/features/leads/stores/followUpNotificationSlice";
import { pushDueInstallment } from "@/features/leads/stores/installmentNotificationSlice";
import { setCurrentLeadId } from "@/features/leads/stores/leadFunctionalSlice";

/** Payload of one `followup:due` push (server-targeted at personal-scope owners). */
/**
 * Wire shape of `installment:due`. Mirrors the server's
 * `NotificationService.notifyInstallmentDue` payload, which deliberately sends
 * everything the popup renders so the client never has to follow up with a
 * fetch just to show a reminder.
 */
interface InstallmentDuePayload {
  /** `<installmentId>:<arm>:<IST date>` — the dedupe key. Never the bare id. */
  reminderKey: string;
  installmentId: number;
  planId: number;
  leadId: number;
  childId: number;
  arm: "pre" | "due" | "overdue";

  leadName?: string | null;
  phone?: string | null;
  phoneCountryCode?: string | null;
  /** The lead's dedicated WhatsApp number, when it differs from `phone`. */
  whatsapp?: string | null;
  stageName?: string | null;
  dealName?: string | null;

  seq: number;
  ofCount: number;
  amount: number;
  paidAmount?: number;
  outstanding: number;
  /** ISO — the IST calendar day it fell due. */
  dueDate: string;
  /** Present on the `overdue` arm. */
  daysOverdue?: number;
  /** Present on the `pre` arm. */
  daysUntilDue?: number;

  planPaidCount?: number;
  planTotalCount?: number;
  planPaidAmount?: number;
  planTotalAmount?: number;

  /** Other instalments that came due for this user in the same tick. */
  alsoDueCount?: number;
  timestamp: string;
}

interface FollowUpDuePayload {
  followUpId: number;
  leadId: number;
  childId?: number | null;
  leadName?: string | null;
  phone?: string | null;
  phoneCountryCode?: string | null;
  stageName?: string | null;
  note?: string | null;
  /** ISO — the moment the follow-up was due, not when we received it. */
  date: string;
  /** Other follow-ups that came due for this user in the same tick. */
  alsoDueCount?: number;
  timestamp: string;
}

/** "Anita Ravi" → "AR". Two letters max, matching the popup's avatar chip. */
const initialsOf = (name: string): string =>
  name
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .join("")
    .toUpperCase()
    .slice(0, 2);

// REALTIME REFETCH RULE — sockets refetch ONLY when a lead comes IN:
//   - lead:new, source sync completed, lead:recaptured (re-enquiry, server
//     targets assignees only), lead:assignment variant "assigned" (a lead
//     newly assigned to ME);
//   - automation:run_finished ONLY when `actorUserId` is me — the run is the
//     consequence of my own mutation, which returned before it finished.
// Every other lead lifecycle event (status/stage moves, field edits, branch
// transfer, archive/restore, delete, deal delete, other users' automations)
// triggers NO network refetch. Why: prod Postgres is CPU-saturated at peak
// and every mutation by anyone was making every open tab re-pull
// /core/leads + stage-route + list-stats + list-count. Other users' screens
// catch up on their own next fetch (navigation / 3-min staleTime). The
// actor's own views are refreshed by their mutation's React Query
// onSuccess/onSettled invalidations, not by the socket echo.
//
// The bundle that remains is debounced (trailing edge) so a bulk import /
// Meta sync burst coalesces into one refetch per tab. Toasts and per-event
// UI side-effects still fire immediately; only the refetch waits 10–30 s
// (or tab focus — see the hidden-tab deferral below).
const LEAD_REFETCH_DEBOUNCE_MS = 10_000;
const LEAD_REFETCH_MAX_WAIT_MS = 30_000;

/**
 * Past this many distinct leads in one window (a bulk sweep), one prefix
 * bust beats walking the cache once per lead.
 */
const MAX_TARGETED_LEADS = 25;

/** Lead ids a targeted bust is pending for; `true` = every lead. */
type LeadTargets = Set<number> | true;

interface LeadRefetchPending {
  /** Deals list — `refetchChilds(leadId?)`. */
  childs: LeadTargets | null;
  /** Activity timeline + failures banner + feed — `invalidateLeadTimelines`. */
  timeline: LeadTargets | null;
  invalidateLeadCaches: boolean;
  /**
   * A lead came IN: the list's own numbers move with the cards — the header
   * total ("All Leads (N)"), the KPI tiles and any open custom report. Without
   * this the cards picked up a new lead while the header and the report kept
   * the old count beside them (cards 93, header 92, report 92).
   */
  counts: boolean;
}

const emptyPending = (): LeadRefetchPending => ({
  childs: null,
  timeline: null,
  invalidateLeadCaches: false,
  counts: false,
});

let leadRefetchTimer: ReturnType<typeof setTimeout> | null = null;
let leadRefetchFirstScheduled = 0;
let leadRefetchPending = emptyPending();

const addTarget = (
  cur: LeadTargets | null,
  leadId: number | undefined,
): LeadTargets => {
  if (cur === true) return true;
  if (leadId == null) return true; // an event that doesn't say which lead
  const set = cur ?? new Set<number>();
  set.add(leadId);
  return set.size > MAX_TARGETED_LEADS ? true : set;
};

const runTargeted = (
  targets: LeadTargets | null,
  fn: (leadId?: number) => void,
) => {
  if (!targets) return;
  if (targets === true) fn();
  else for (const id of targets) fn(id);
};

/**
 * Everything the bundle can touch. In a HIDDEN tab these are only marked
 * stale (`refetchType: "none"`) — no fetch — and the bundle re-runs once the
 * tab is visible again. A background tab was paying the same refetch storm
 * as the one the user is looking at.
 */
const LEAD_BUNDLE_PREFIXES: readonly (readonly string[])[] = [
  ["leads"],
  ["lead-board"],
  ["get-leads"],
  ["get-child"],
  ["childs"],
  ["leads-list-count"],
  ["leads-list-stats"],
  ["builder-run"],
  ["activity-log"],
  ["lead-automation-failures"],
];

const isTabVisible = () =>
  typeof document === "undefined" || document.visibilityState === "visible";

let leadRefetchWaitingForVisible = false;

const onTabVisibleRunBundle = () => {
  if (!isTabVisible()) return;
  document.removeEventListener("visibilitychange", onTabVisibleRunBundle);
  leadRefetchWaitingForVisible = false;
  if (leadRefetchTimer) clearTimeout(leadRefetchTimer);
  runLeadRefetchBundle();
};

const runLeadRefetchBundle = () => {
  leadRefetchTimer = null;
  leadRefetchFirstScheduled = 0;

  if (!isTabVisible()) {
    // Mark stale, fetch nothing, keep the pending targets — the tab will
    // run the whole bundle the moment the user comes back to it.
    for (const queryKey of LEAD_BUNDLE_PREFIXES) {
      queryClient.invalidateQueries({ queryKey, refetchType: "none" });
    }
    if (!leadRefetchWaitingForVisible) {
      leadRefetchWaitingForVisible = true;
      document.addEventListener("visibilitychange", onTabVisibleRunBundle);
    }
    return;
  }

  const pending = leadRefetchPending;
  leadRefetchPending = emptyPending();

  // Each query is invalidated ONCE per bundle. Overlapping prefixes in one
  // tick cancel and re-send the in-flight GET, and our query fns ignore the
  // abort signal, so every overlap was an extra server request.
  if (pending.invalidateLeadCaches) {
    // Covers ["leads"] (grid / infinite / board / stage-board / stage-route),
    // the header count + KPI tiles and the whole ["childs"] prefix, so no
    // per-lead deals bust on top.
    invalidateLeadCaches();
  } else {
    refetchLeads(); // ["leads"] — same sub-keys as above
    runTargeted(pending.childs, refetchChilds); // disjoint per-lead predicates
    if (pending.counts) {
      queryClient.invalidateQueries({ queryKey: ["leads-list-count"] });
      queryClient.invalidateQueries({ queryKey: ["leads-list-stats"] });
    }
  }
  // Any lead coming IN moves an open custom report's numbers too
  // (useReportResult) — a report beside the leads page must re-ask the
  // server, or the two read numbers from different moments.
  if (pending.counts || pending.invalidateLeadCaches) {
    queryClient.invalidateQueries({ queryKey: ["builder-run"] });
  }
  // Activity timeline + automation-failures banner + follow-ups + feed, exact
  // to the leads the events named (my own automation runs), in one pass.
  if (pending.timeline) {
    invalidateLeadTimelines(pending.timeline === true ? null : pending.timeline);
  }
};

interface ScheduleLeadRefetchOpts {
  childs?: boolean;
  timeline?: boolean;
  invalidateLeadCaches?: boolean;
  /** A lead came in: refresh the header count, KPI tiles and report runs. */
  counts?: boolean;
  /**
   * The lead the event is about. When given, `childs` / `timeline` bust ONLY
   * that lead's queries; when absent they bust the prefix (an event that
   * doesn't carry a lead id).
   */
  leadId?: number | null;
}

/** `payload.leadId` → a usable id, or undefined when the event has none. */
const leadIdOf = (raw: unknown): number | undefined => {
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : undefined;
};

const scheduleLeadRefetch = (opts?: ScheduleLeadRefetchOpts) => {
  const leadId = leadIdOf(opts?.leadId);
  if (opts?.childs) {
    leadRefetchPending.childs = addTarget(leadRefetchPending.childs, leadId);
  }
  if (opts?.timeline) {
    leadRefetchPending.timeline = addTarget(leadRefetchPending.timeline, leadId);
  }
  if (opts?.invalidateLeadCaches) leadRefetchPending.invalidateLeadCaches = true;
  if (opts?.counts) leadRefetchPending.counts = true;

  const now = Date.now();
  if (leadRefetchFirstScheduled === 0) leadRefetchFirstScheduled = now;

  if (leadRefetchTimer) clearTimeout(leadRefetchTimer);

  // Cap the wait so a long, steady stream of events still fires within
  // LEAD_REFETCH_MAX_WAIT_MS rather than perpetually deferring.
  const elapsed = now - leadRefetchFirstScheduled;
  const remainingMax = Math.max(0, LEAD_REFETCH_MAX_WAIT_MS - elapsed);
  const wait = Math.min(LEAD_REFETCH_DEBOUNCE_MS, remainingMax);

  leadRefetchTimer = setTimeout(runLeadRefetchBundle, wait);
};

/**
 * Used only for MY OWN automation runs (see AUTOMATION_RUN_FINISHED).
 * Immediate, targeted refresh of everything the view-lead drawer renders for
 * ONE lead: the lead payload (stage strip, status, assignees, custom fields,
 * `lead.child`), the deals list + current deal, the card hover list, the
 * activity timeline / automation-failures banner / follow-ups, and the Tracks
 * tab. Every bust is keyed or predicated on THIS lead's id and active-only,
 * so it costs nothing when that lead isn't on screen — and a drawer open on
 * a DIFFERENT lead is not touched either.
 */
const refreshOpenLeadDrawer = (leadId: number) => {
  queryClient.invalidateQueries({
    predicate: (q) =>
      q.queryKey[0] === "get-leads" && Number(q.queryKey[1]) === leadId,
  });
  refetchChilds(leadId);
  // The current deal is cached as `["get-child", childId]`; the row itself
  // carries its parent id, which is the only handle we have from a lead id.
  queryClient.invalidateQueries({
    predicate: (q) =>
      q.queryKey[0] === "get-child" &&
      Number((q.state.data as { leadId?: unknown } | undefined)?.leadId) ===
        leadId,
  });
  queryClient.invalidateQueries({ queryKey: ["lead-children-hover", leadId] });
  invalidateLeadTimeline(leadId);
  refetchEntityTracksAfterAutomation();
};

/** One-line body preview for a WhatsApp toast. */
const whatsappPreview = (payload: any): string => {
  const server = typeof payload?.preview === "string" ? payload.preview : "";
  if (server) return server.slice(0, 60);
  const body = payload?.message?.body;
  if (typeof body === "string" && body.trim()) return body.slice(0, 60);
  const type = payload?.message?.type;
  return type && type !== "text" ? `Sent ${type}` : "New WhatsApp message";
};

export const useGlobalSocketEvents = () => {
  // -------------------------------------------
  // 1. LEAD: Refetch Logic
  // -------------------------------------------
  useSocketListener(SocketEvents.NEW_LEAD, () => {
    toast.success(`New Lead Assigned`, {
      duration: 4000,
    });
    // The cards AND the numbers beside them: header total, KPI tiles, an
    // open custom report.
    scheduleLeadRefetch({ counts: true });
    // WhatsApp contact list is its own cache key — invalidate immediately
    // (cheap) so unread-thread bubbles refresh without waiting on the
    // debounced bundle.
    refetchWhatsappLeads();
  });

  useSocketListener(
    SocketEvents.LEAD_ASSIGNMENT,
    (payload: {
      variant?: "refresh" | "assigned" | "revoked";
      leadName?: string;
      childId?: number;
    }) => {
      const variant = payload?.variant ?? "refresh";
      // Only "assigned" (a lead newly assigned to ME) refetches. "revoked" /
      // "refresh" are other users' actions — toast only, no network.
      if (variant === "assigned") {
        scheduleLeadRefetch({ childs: true, invalidateLeadCaches: true });
      }
      // The bell is NOT touched here: the server sends the inbox row as its
      // own `system:notification` push, which patches the badge in place.

      if (variant === "revoked") {
        const name = payload?.leadName?.trim();
        toast.warning(
          name
            ? `You no longer have access to “${name}” — your assignment was replaced.`
            : "You were removed from a lead or deal assignment.",
          { duration: 6000 },
        );
        return;
      }
      if (variant === "assigned") {
        const name = payload?.leadName?.trim();
        const place = payload?.childId != null ? "deal" : "lead";
        toast.success(
          name
            ? `You were assigned to ${place} “${name}”.`
            : `You have a new ${place} assignment.`,
          { duration: 5000 },
        );
      }
    },
  );

  useSocketListener(SocketEvents.SOURCE_SYNC_COMPLETED, () => {
    toast.success(`Leads are successfully synced`);
    scheduleLeadRefetch({ counts: true });
  });

  // Stage/status notification tone — the server already resolved the tone +
  // role targeting, so every received event is meant for this user. The
  // coalescer turns a bulk sweep's burst into ONE ring + one "N leads" popup.
  useSocketListener(
    SocketEvents.NOTIFICATION_TONE,
    (payload: ToneAlertPayload) => {
      enqueueToneAlert(payload);
    },
  );

  // A lead assigned to this user re-enquired through a source. Server targets
  // assignees only; the coalescer batches a sync run's burst into one modal.
  useSocketListener(
    SocketEvents.LEAD_RECAPTURED,
    (payload: RecaptureAlertPayload) => {
      enqueueRecaptureAlert(payload);
      // Re-enquiry bumps counters/badges; the inbox row arrives on its own
      // `system:notification` push.
      scheduleLeadRefetch({ invalidateLeadCaches: true });
    },
  );

  // A follow-up this user owns just came due. The server decides "due" (its
  // per-minute sweep) and targets personal-scope owners only, so we pop the
  // modal for whatever arrives — no client-side clock, no scope re-check.
  //
  // `store.dispatch` rather than useAppDispatch: this hook runs in AppProviders
  // ABOVE <ReduxProvider>, so there is no Provider in scope. The store is a
  // module singleton and FollowUpNotifier reads it from inside the Provider.
  useSocketListener(SocketEvents.FOLLOWUP_DUE, (payload: FollowUpDuePayload) => {
    if (payload?.followUpId == null || payload?.leadId == null) return;
    const leadName = payload.leadName || "Lead";
    store.dispatch(
      pushDueFollowUp({
        id: String(payload.followUpId),
        leadId: payload.leadId,
        childId: payload.childId ?? null,
        leadName,
        leadInitials: initialsOf(leadName),
        date: payload.date,
        note: payload.note ?? "",
        stage: payload.stageName ?? undefined,
        phone: payload.phone ?? undefined,
        phoneCountryCode: payload.phoneCountryCode ?? undefined,
        alsoDueCount: payload.alsoDueCount ?? 0,
      }),
    );
    // The inbox row for this reminder arrives on its own
    // `system:notification` push — nothing to refetch here.
  });

  // An instalment on a deal's EMI plan needs attention. Three arms reach this
  // one event — a pre-reminder, the due day, and a once-per-IST-day overdue
  // nudge — and `arm` says which.
  //
  // `reminderKey` is the dedupe key and it is NOT the instalment id: the same
  // instalment legitimately rings many times, so keying on its id would show
  // the first alert and silently swallow every one after it. The server mints
  // the key as `<installmentId>:<arm>:<IST date>`.
  useSocketListener(
    SocketEvents.INSTALLMENT_DUE,
    (payload: InstallmentDuePayload) => {
      if (payload?.installmentId == null || payload?.leadId == null) return;
      if (!payload.reminderKey) return; // an un-keyed push would break dedupe
      const leadName = payload.leadName || "Lead";
      store.dispatch(
        pushDueInstallment({
          reminderKey: payload.reminderKey,
          installmentId: payload.installmentId,
          planId: payload.planId,
          leadId: payload.leadId,
          childId: payload.childId,
          arm: payload.arm,
          leadName,
          leadInitials: initialsOf(leadName),
          phone: payload.phone ?? null,
          phoneCountryCode: payload.phoneCountryCode ?? null,
          whatsapp: payload.whatsapp ?? null,
          stage: payload.stageName ?? null,
          dealName: payload.dealName ?? null,
          seq: payload.seq,
          ofCount: payload.ofCount,
          amount: payload.amount,
          paidAmount: payload.paidAmount ?? 0,
          outstanding: payload.outstanding,
          dueDate: payload.dueDate,
          daysOverdue: payload.daysOverdue,
          daysUntilDue: payload.daysUntilDue,
          planPaidCount: payload.planPaidCount ?? 0,
          planTotalCount: payload.planTotalCount ?? payload.ofCount,
          planPaidAmount: payload.planPaidAmount ?? 0,
          planTotalAmount: payload.planTotalAmount ?? 0,
          alsoDueCount: payload.alsoDueCount ?? 0,
        }),
      );
      // The inbox row arrives on its own `system:notification` push. An open
      // Payments tab should flip the row to overdue without a manual refresh
      // — this user's deal, so target its lead.
      refetchChilds(payload.leadId);
    },
  );

  // LEAD_STATUS_UPDATED, LEAD_UPDATED, LEAD_BRANCH_TRANSFERRED, LEAD_DELETED,
  // LEAD_RESTORED: deliberately NOT listened to — no refetch for other users'
  // changes (see the REALTIME REFETCH RULE at the top). The actor's own
  // mutations refresh their views in onSuccess.

  // An automation run finished. Refetch ONLY when the run was triggered by MY
  // action (`actorUserId === me`): automations run after my mutation
  // returned, so its onSuccess could not see the run's writes (a deal moved
  // by "Move deal", a field / assignee / track / follow-up). Anyone else's
  // run, a system/webhook/cron run (`actorUserId: null`) or an old server
  // without the field → no network.
  useSocketListener(
    SocketEvents.AUTOMATION_RUN_FINISHED,
    (payload: {
      leadId?: number;
      childId?: number;
      actorUserId?: number | null;
    }) => {
      const me = currentUserId();
      const actor = Number(payload?.actorUserId);
      if (me == null || !Number.isFinite(actor) || actor !== me) return;
      // The open drawer shows the outcome NOW, targeted at the run's lead and
      // active-only. The server awaits its deal-list cache bust BEFORE
      // pushing this event, so the refetch reads the post-run rows.
      const leadId = leadIdOf(payload?.leadId);
      if (leadId != null) refreshOpenLeadDrawer(leadId);
      // Lists / board / counts on the debounced bundle; the deal list +
      // timeline busts are exact to this run's lead.
      scheduleLeadRefetch({ timeline: true, childs: true, leadId });
    },
  );

  // A customer PAID a Razorpay payment link: the webhook has already recorded
  // the payment on the deal. Not a lead lifecycle event — it is money arriving
  // from outside, a handful a day — so the screens SHOWING it refresh now
  // (owner, 2026-10-21: "if paid we need it to reflect in the CRM in real
  // time"). Every bust is active-only: an open drawer on that lead (deal,
  // payments, links, timeline) and the money cards (dashboard payments + KPI
  // strip, Reports revenue / receivables). No lead list, board or count is
  // refetched — those follow the rule above. The person who sent the link,
  // or anyone with the lead open, gets a toast.
  useSocketListener(
    SocketEvents.PAYMENT_LINK_PAID,
    (payload: {
      leadId?: number;
      amount?: number;
      createdById?: number | null;
      leadName?: string | null;
      dealName?: string | null;
      /** "Enrolment · Enrolled" — the deal moved forward on payment. */
      movedTo?: string | null;
    }) => {
      const leadId = leadIdOf(payload?.leadId);
      const leadOpen =
        leadId != null &&
        queryClient
          .getQueryCache()
          .findAll({
            predicate: (q) =>
              q.queryKey[0] === "get-leads" &&
              Number(q.queryKey[1]) === leadId &&
              q.getObserversCount() > 0,
          }).length > 0;
      if (leadId != null) refreshOpenLeadDrawer(leadId);
      for (const key of [
        "payments",
        "payment-summary",
        "razorpay-links",
        // The dialog's "When paid … moved to" line — the deal may have moved.
        "razorpay-paid-move",
        "v2-payment-insights",
        "v2-collections",
        "v2-kpi-summary",
        "v2-report-revenue",
        "v2-report-receivables",
      ]) {
        queryClient.invalidateQueries({ queryKey: [key] });
      }

      const me = currentUserId();
      const sentByMe = me != null && Number(payload?.createdById) === me;
      if (sentByMe || leadOpen) {
        const amount = Number(payload?.amount);
        const who = payload?.leadName ? ` from ${payload.leadName}` : "";
        const deal = payload?.dealName ? ` · ${payload.dealName}` : "";
        toast.success(
          `Payment received${who}: ₹${Number.isFinite(amount) ? amount.toLocaleString("en-IN") : ""}${deal}`,
          {
            description: payload?.movedTo
              ? `Paid through the Razorpay link — recorded on the deal and moved to ${payload.movedTo}.`
              : "Paid through the Razorpay link — recorded on the deal.",
            duration: 8000,
            ...(leadId != null && !leadOpen
              ? {
                  action: {
                    label: "Open",
                    // The notification hosts' way to open a lead's drawer.
                    onClick: () => {
                      store.dispatch(setCurrentLeadId(leadId));
                      if (!window.location.pathname.startsWith("/leads")) {
                        globalNavigate("/leads");
                      }
                    },
                  },
                }
              : {}),
          },
        );
      }
    },
  );

  // -------------------------------------------
  // 2. DEAL DELETION: zero-network local patch only
  // -------------------------------------------
  // `removeChildFromCaches` strips the deal from the board / stage-route /
  // lead-detail / deals caches already in memory, so it vanishes on the next
  // paint without a fetch. No refetch (REALTIME REFETCH RULE); authoritative
  // counts catch up on the next natural fetch. Idempotent, so the deleting
  // user re-running it on their own broadcast is a harmless no-op.
  useSocketListener(
    SocketEvents.CHILD_DELETED,
    (payload: { childId?: number; leadId?: number }) => {
      const childId = payload?.childId;
      if (childId == null) return;
      removeChildFromCaches(queryClient, childId);
    },
  );

  // A WhatsApp message landed in a lead's thread. The server fans this out for
  // BOTH directions (the coexistence echo replays our own outbound sends), so
  // only INBOUND rings — nobody wants an alert for their own reply.
  useSocketListener(SocketEvents.WHATSAPP_MESSAGE_NEW, (payload: any) => {
    const leadId = payload?.leadId;
    refetchCurrentWhatsappMessage(leadId);
    // Sidebar ordering + unread bubbles live on their own cache key.
    refetchWhatsappLeads();

    const direction = String(
      payload?.direction ?? payload?.message?.direction ?? "INBOUND",
    ).toUpperCase();
    if (direction === "OUTBOUND") return;

    playChatSound();
    showNewMessageToast({
      senderName: payload?.leadName || "WhatsApp",
      preview: whatsappPreview(payload),
      variant: "whatsapp",
      // Click-through: land on the WhatsApp inbox with THIS chat open.
      // openChatForLead flips the inbox tab + sets pendingChatLeadId, which
      // useWhatsappChat resolves to the contact (creating a virtual row for
      // a brand-new conversation), so it works from any page.
      onClick:
        leadId != null
          ? () => {
              store.dispatch(openChatForLead(Number(leadId)));
              globalNavigate("/whatsapp");
            }
          : undefined,
    });
  });

  // -------------------------------------------
  // 3. INTERNAL CHAT: Custom toast + sound + badge refresh
  // -------------------------------------------
  useSocketListener(SocketEvents.INTERNAL_CHAT_NEW, (data: any) => {
    const msg = data?.message;
    // The server targets every user on the lead — which includes the sender.
    // Alerting them for their own message is just noise, so only the recipients
    // ring. Cache invalidation below still runs for everyone.
    const myId = currentUserId();
    const isOwnMessage =
      msg?.senderId != null && myId != null && Number(msg.senderId) === myId;
    // "Chat & notes sync": the chat copy of a NOTE. The note already rang its
    // audience (inbox row + note popup), so its copy only refreshes the thread
    // and the badge below — one alert per thing said.
    const isNoteCopy = data?.syncedFrom === "note";

    if (msg && !isOwnMessage && !isNoteCopy && claimChatAlert(msg)) {
      playChatSound();

      const mentions = msg.mentions as
        | { userId: number; name: string }[]
        | null;
      // `mentionUserIds` is the push's own copy of the mention audience. It
      // exists because a child message's All-Chats MIRROR stores no mentions,
      // and either copy can reach us first — reading the list off the message
      // alone made "did I get popped for this @-mention?" a socket-ordering
      // coin flip. Falls back to the message for an older server.
      const mentionIds: number[] | null = Array.isArray(data?.mentionUserIds)
        ? data.mentionUserIds.map((id: unknown) => Number(id))
        : null;
      const mentioned =
        myId != null &&
        (mentionIds
          ? mentionIds.includes(myId)
          : !!mentions?.some((m) => Number(m.userId) === myId));

      // `popupUserIds` is the server's "pop these people" list for an
      // ATTACHMENT — the lead's and/or the deal's assignees, per Settings →
      // Manage → "Pop assigned users". It rides the same push (never a second
      // event) so there is one decision per message and no toast-then-modal
      // race. Absent on a text message and on an older server.
      const popupUserIds: number[] = Array.isArray(data?.popupUserIds)
        ? data.popupUserIds.map((id: unknown) => Number(id))
        : [];
      const popsForAttachment =
        myId != null && popupUserIds.includes(myId) && msg.type !== "text";

      // A mention outranks an attachment: someone asked YOU something, and a
      // mentioned user who is also an assignee must get one popup, not two.
      const popupKind: ChatPopupKind | null = mentioned
        ? "mention"
        : popsForAttachment
          ? "attachment"
          : null;

      const leadId = data?.leadId != null ? Number(data.leadId) : null;
      const childId =
        msg?.channel === "child" && msg?.childId != null
          ? Number(msg.childId)
          : null;

      if (popupKind && leadId != null) {
        pushChatPopupAlert({
          kind: popupKind,
          key: chatAlertKey(msg),
          leadId,
          leadName: data?.leadName ?? null,
          childId,
          messageId: msg?.id ? String(msg.id) : undefined,
          senderName: msg.sender?.name || "Someone",
          // A mention can ride a file ("@Priya here's the CAS") — with no
          // caption there is no body to quote, so fall back to the file name.
          preview:
            popupKind === "mention"
              ? (msg.body || "").slice(0, 280) || msg.mediaName || ""
              : msg.mediaName || "",
          // The All-Chats mirror of an attachment is stored as type
          // "system", so the mime is what tells an image from a voice note.
          mediaKind:
            msg.type === "image" ||
            String(msg.mediaMime || "").startsWith("image/")
              ? "image"
              : msg.type === "audio" ||
                  String(msg.mediaMime || "").startsWith("audio/")
                ? "audio"
                : "file",
          at: Date.now(),
        });
      } else {
        showNewMessageToast({
          senderName: msg.sender?.name || "Someone",
          preview:
            msg.type === "text"
              ? (msg.body || "").slice(0, 60)
              : msg.type === "audio"
                ? "Sent a voice note"
                : msg.type === "image"
                  ? "Sent an image"
                  : "Sent a file",
          mentioned,
          variant: "internal",
          // Click-through: pop the header chat open ON this lead's thread,
          // input focused (chatOpenBus → ChatDropdown).
          onClick:
            leadId != null
              ? () =>
                  requestOpenChat({
                    leadId,
                    leadName: data?.leadName ?? undefined,
                    // Land on the message itself, on the channel it was sent
                    // in — a mention toast must open AT the mention.
                    messageId: msg?.id ? String(msg.id) : undefined,
                    childId,
                  })
              : undefined,
        });
      }
    }
    // Refresh unread count and thread list
    queryClient.invalidateQueries({ queryKey: ["chat-unread-count"] });
    queryClient.invalidateQueries({ queryKey: ["chat-threads"] });
  });

  // -------------------------------------------
  // 3b. NOTE ADDED: the same interrupting popup as a chat attachment
  // -------------------------------------------
  // The server targets this at the note's audience only (lead assignees +
  // creator, plus the deal's assignees for a deal note) and never at the
  // author, and the org-wide "Pop assigned users → Notes" switch is applied
  // server-side — so anything that lands here is meant for this user. The
  // payload carries the note text: NO refetch (REALTIME REFETCH RULE — a
  // note is not a new lead). Clicking the popup opens the lead, and THAT is
  // when the drawer refreshes.
  useSocketListener(
    SocketEvents.NOTE_CREATED,
    (payload: {
      noteId?: number;
      leadId?: number;
      childId?: number | null;
      leadName?: string | null;
      authorId?: number;
      authorName?: string | null;
      content?: string;
    }) => {
      const leadId = leadIdOf(payload?.leadId);
      if (leadId == null) return;
      // Belt and braces: the server drops the author, but a stale server (or
      // the author's own second tab) must not pop "you added a note".
      const myId = currentUserId();
      if (myId != null && Number(payload?.authorId) === myId) return;

      playChatSound();
      pushChatPopupAlert({
        kind: "note",
        key: `note:${payload?.noteId ?? `${leadId}:${Date.now()}`}`,
        leadId,
        leadName: payload?.leadName ?? null,
        childId:
          payload?.childId != null && Number(payload.childId) > 0
            ? Number(payload.childId)
            : null,
        noteId: payload?.noteId != null ? Number(payload.noteId) : undefined,
        senderName: payload?.authorName || "Someone",
        preview: String(payload?.content ?? "").slice(0, 280),
        at: Date.now(),
      });
    },
  );

  // Cross-device sync: when this user reads on another device, the server emits
  // INTERNAL_CHAT_READ — refresh the badge here too. Other users' read receipts
  // shouldn't move our unread count, so gate on userId.
  useSocketListener(SocketEvents.INTERNAL_CHAT_READ, (payload: any) => {
    const myId = currentUserId();
    if (myId == null || Number(payload?.userId) !== myId) return;
    queryClient.invalidateQueries({ queryKey: ["chat-unread-count"] });
    queryClient.invalidateQueries({ queryKey: ["chat-threads"] });
  });

  // -------------------------------------------
  // 4. NOTIFICATIONS: patch the bell cache from the push — never a GET
  // -------------------------------------------
  // `created` carries the row itself (and this recipient's unread count,
  // or `null` = "increment what you have"); `read` / `dismissed` / `cleared`
  // carry the id. Invalidating the prefix here was 3 list GETs per push per
  // tab × ~55 recipients per event — 475k GET /core/notifications a day.
  useSocketListener(SocketEvents.NOTIFICATION, (payload: NotificationPush) => {
    applyNotificationPush(payload);
    // The pre-shuffle warning modal lives on its own key and only a warning
    // row can change what it shows.
    if (
      payload?.action === "created" &&
      payload.notification?.type === "automation_shuffle_warning"
    ) {
      queryClient.invalidateQueries({ queryKey: SHUFFLE_WARNINGS_KEY });
    }
  });

  // ALERTS — server targets recipients only. The cache patch puts the row in
  // the Alert box and bumps the badge on the push itself (no GET); an
  // ANNOUNCE alert also pops the modal (AlertAnnounceHost), a SILENT one
  // rings the same corner toast as a chat message.
  useSocketListener(SocketEvents.ALERT_NEW, (payload: AlertNewPush) => {
    applyAlertNewPush(payload);
  });
  useSocketListener(SocketEvents.ALERT_UPDATED, (payload: AlertUpdatedPush) => {
    applyAlertUpdatedPush(payload);
  });

  // -------------------------------------------
  // 5. RBAC / branch / CF access (server targets affected user rooms only)
  // -------------------------------------------
  useSocketListener(SocketEvents.USER_ACCESS_REFRESH, () => {
    void refreshSessionUserAndInvalidateQueries();
  });
};
